import React, { useState } from 'react';
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalCloseButton,
  Box,
  Text,
  Flex,
  VStack,
  HStack,
  Badge,
  Spinner,
  Icon,
  IconButton,
  Tooltip,
  Divider,
  Collapse,
  useClipboard,
  Input,
} from '@chakra-ui/react';
import {
  Link as LinkIcon,
  Clock,
  Eye,
  ArrowLeft,
  MapPin,
  DeviceMobile,
  Calendar,
  X,
  Copy,
  Check,
} from 'phosphor-react';
import { MapContainer, TileLayer, Marker, Popup, Circle } from 'react-leaflet';
import L from 'leaflet';
import { useQuery } from '@tanstack/react-query';
import moment from 'moment';
import { api } from '../../../../services/api';
import {
  ILinksLogsModalProps,
  ILinkTemporario,
  ILinkAcesso,
  ILinksTemporariosResponse,
  ILinkAcessosResponse,
} from '../utils/dossieTypes';

// Fix para o icone do marker do Leaflet
const markerIcon = new L.Icon({
  iconUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png',
  iconRetinaUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon-2x.png',
  shadowUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png',
  iconSize: [25, 41],
  iconAnchor: [12, 41],
  popupAnchor: [1, -34],
  shadowSize: [41, 41],
});

export function LinksLogsModal({ isOpen, onClose, codiEmp }: ILinksLogsModalProps) {
  const [selectedLink, setSelectedLink] = useState<ILinkTemporario | null>(null);
  const [expandedMapId, setExpandedMapId] = useState<number | null>(null);

  // Gera o link completo baseado no hashed_link
  const getFullLink = (hashedLink: string) => {
    const baseUrl = window.location.origin;
    return `${baseUrl}/dossie/${hashedLink}`;
  };

  // Clipboard para o link selecionado
  const selectedLinkUrl = selectedLink ? getFullLink(selectedLink.hashed_link) : '';
  const { hasCopied: hasCopiedLink, onCopy: onCopyLink } = useClipboard(selectedLinkUrl);

  // Query para buscar links temporários da empresa
  const {
    data: linksData,
    isLoading: isLoadingLinks,
    error: linksError,
  } = useQuery<ILinksTemporariosResponse>({
    queryKey: ['links-temporarios', codiEmp],
    queryFn: async () => {
      const res = await api.get(`/api/v1/clientes/links-temporarios/empresa/${codiEmp}`);
      return res.data;
    },
    enabled: isOpen && !selectedLink,
  });

  // Query para buscar acessos de um link específico
  const {
    data: acessosData,
    isLoading: isLoadingAcessos,
    error: acessosError,
  } = useQuery<ILinkAcessosResponse>({
    queryKey: ['link-acessos', selectedLink?.id],
    queryFn: async () => {
      const res = await api.get(`/api/v1/clientes/links-temporarios/${selectedLink?.id}/acessos`);
      return res.data;
    },
    enabled: !!selectedLink,
  });

  const links = linksData?.data || [];
  const acessos = acessosData?.data || [];

  // Verifica se link está expirado
  const isExpired = (expiresAt: string) => moment(expiresAt).isBefore(moment());

  // Formata data
  const formatDate = (date: string | null) =>
    date ? moment(date).format('DD/MM/YYYY HH:mm') : '-';

  // Reset ao fechar
  const handleClose = () => {
    setSelectedLink(null);
    onClose();
  };

  // Renderiza lista de links
  const renderLinksList = () => {
    if (isLoadingLinks) {
      return (
        <Flex justify="center" py={8}>
          <Spinner size="lg" color="blue.500" />
        </Flex>
      );
    }

    if (linksError) {
      return (
        <Box textAlign="center" py={8}>
          <Text color="red.500">Erro ao carregar links</Text>
        </Box>
      );
    }

    if (links.length === 0) {
      return (
        <Box textAlign="center" py={8}>
          <Icon as={LinkIcon} boxSize={10} color="gray.300" mb={3} />
          <Text color="gray.500">Nenhum link temporario criado</Text>
        </Box>
      );
    }

    return (
      <VStack spacing={3} align="stretch">
        {links.map(link => {
          const expired = isExpired(link.expires_at);
          return (
            <Box
              key={link.id}
              p={4}
              bg={expired ? 'gray.50' : 'white'}
              border="1px"
              borderColor={expired ? 'gray.200' : 'blue.200'}
              borderRadius="lg"
              cursor="pointer"
              _hover={{ bg: expired ? 'gray.100' : 'blue.50', borderColor: 'blue.400' }}
              onClick={() => setSelectedLink(link)}
              transition="all 0.2s"
            >
              <Flex justify="space-between" align="center">
                <HStack spacing={3}>
                  <Icon as={LinkIcon} boxSize={5} color={expired ? 'gray.400' : 'blue.500'} />
                  <Box>
                    <HStack spacing={2} mb={1}>
                      <Text fontSize="sm" fontWeight="medium" color="gray.700" fontFamily="mono">
                        {link.hashed_link.substring(0, 12)}...
                      </Text>
                      <Badge colorScheme={expired ? 'gray' : 'green'} size="sm">
                        {expired ? 'Expirado' : 'Ativo'}
                      </Badge>
                    </HStack>
                    <HStack spacing={4} fontSize="xs" color="gray.500">
                      <HStack spacing={1}>
                        <Icon as={Calendar} boxSize={3} />
                        <Text>Criado: {formatDate(link.dt_criacao)}</Text>
                      </HStack>
                      <HStack spacing={1}>
                        <Icon as={Clock} boxSize={3} />
                        <Text>Expira: {formatDate(link.expires_at)}</Text>
                      </HStack>
                    </HStack>
                  </Box>
                </HStack>
                <Tooltip label="Ver acessos">
                  <IconButton
                    aria-label="Ver acessos"
                    icon={<Eye size={18} />}
                    size="sm"
                    variant="ghost"
                    colorScheme="blue"
                  />
                </Tooltip>
              </Flex>
            </Box>
          );
        })}
      </VStack>
    );
  };

  // Renderiza logs de acesso
  const renderAcessosView = () => {
    if (isLoadingAcessos) {
      return (
        <Flex justify="center" py={8}>
          <Spinner size="lg" color="blue.500" />
        </Flex>
      );
    }

    if (acessosError) {
      return (
        <Box textAlign="center" py={8}>
          <Text color="red.500">Erro ao carregar acessos</Text>
        </Box>
      );
    }

    return (
      <VStack spacing={4} align="stretch">
        {/* Seção de copiar link */}
        <Box p={4} bg="blue.50" borderRadius="md" border="1px" borderColor="blue.200">
          <Text fontSize="sm" fontWeight="semibold" color="blue.700" mb={2}>
            Link de acesso
          </Text>
          <Flex gap={2} align="center">
            <Input value={selectedLinkUrl} isReadOnly fontSize="xs" bg="white" flex={1} size="sm" />
            <IconButton
              aria-label="Copiar link"
              icon={hasCopiedLink ? <Check size={16} /> : <Copy size={16} />}
              colorScheme={hasCopiedLink ? 'green' : 'gray'}
              variant="ghost"
              size="sm"
              onClick={onCopyLink}
            />
          </Flex>
          <Text fontSize="xs" color="gray.500" mt={2}>
            A senha foi exibida apenas no momento da criação do link.
          </Text>
        </Box>

        <Divider />

        {/* Lista de acessos */}
        {acessos.length === 0 ? (
          <Box textAlign="center" py={8}>
            <Icon as={Eye} boxSize={10} color="gray.300" mb={3} />
            <Text color="gray.500">Nenhum acesso registrado</Text>
          </Box>
        ) : (
          <VStack spacing={3} align="stretch">
            <Text fontSize="sm" fontWeight="semibold" color="gray.700">
              Histórico de acessos ({acessos.length})
            </Text>
            {acessos.map((acesso: ILinkAcesso) => (
              <Box
                key={acesso.id}
                p={4}
                bg="white"
                border="1px"
                borderColor="gray.200"
                borderRadius="lg"
              >
                <Flex justify="space-between" align="flex-start" mb={3}>
                  <HStack spacing={2}>
                    <Badge colorScheme="blue" fontSize="xs">
                      IP: {acesso.ip_cliente}
                    </Badge>
                    <Text fontSize="xs" color="gray.500">
                      {formatDate(acesso.dt_criacao)}
                    </Text>
                  </HStack>
                </Flex>

                <Flex wrap="wrap" gap={4} fontSize="xs" color="gray.600">
                  {acesso.user_agent && (
                    <HStack spacing={1}>
                      <Icon as={DeviceMobile} boxSize={3} color="purple.500" />
                      <Text>{acesso.user_agent}</Text>
                    </HStack>
                  )}

                  {acesso.latitude && acesso.longitude && (
                    <Tooltip label={expandedMapId === acesso.id ? 'Fechar mapa' : 'Ver no mapa'}>
                      <HStack
                        spacing={1}
                        cursor="pointer"
                        onClick={() =>
                          setExpandedMapId(expandedMapId === acesso.id ? null : acesso.id)
                        }
                        _hover={{ color: 'green.600' }}
                        transition="color 0.2s"
                      >
                        <Icon as={MapPin} boxSize={3} color="green.500" />
                        <Text>
                          {parseFloat(acesso.latitude).toFixed(4)},{' '}
                          {parseFloat(acesso.longitude).toFixed(4)}
                          {acesso.accuracy && ` (${acesso.accuracy.toFixed(0)}m)`}
                        </Text>
                      </HStack>
                    </Tooltip>
                  )}
                </Flex>

                {/* Mini Mapa */}
                {acesso.latitude && acesso.longitude && (
                  <Collapse in={expandedMapId === acesso.id} animateOpacity>
                    <Box mt={3} borderRadius="lg" overflow="hidden" position="relative">
                      <IconButton
                        aria-label="Fechar mapa"
                        icon={<X size={14} />}
                        size="xs"
                        position="absolute"
                        top={2}
                        right={2}
                        zIndex={1000}
                        bg="white"
                        shadow="md"
                        onClick={() => setExpandedMapId(null)}
                      />
                      <MapContainer
                        center={[parseFloat(acesso.latitude), parseFloat(acesso.longitude)]}
                        zoom={15}
                        style={{ height: '200px', width: '100%' }}
                        scrollWheelZoom={false}
                      >
                        <TileLayer
                          attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
                          url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                        />
                        <Marker
                          position={[parseFloat(acesso.latitude), parseFloat(acesso.longitude)]}
                          icon={markerIcon}
                        >
                          <Popup>
                            <Text fontSize="xs" fontWeight="bold">
                              Acesso em {formatDate(acesso.dt_criacao)}
                            </Text>
                            <Text fontSize="xs">IP: {acesso.ip_cliente}</Text>
                          </Popup>
                        </Marker>
                        {acesso.accuracy && (
                          <Circle
                            center={[parseFloat(acesso.latitude), parseFloat(acesso.longitude)]}
                            radius={acesso.accuracy}
                            pathOptions={{ color: 'green', fillColor: 'green', fillOpacity: 0.1 }}
                          />
                        )}
                      </MapContainer>
                    </Box>
                  </Collapse>
                )}
              </Box>
            ))}
          </VStack>
        )}
      </VStack>
    );
  };

  return (
    <Modal isOpen={isOpen} onClose={handleClose} size="lg" scrollBehavior="inside">
      <ModalOverlay />
      <ModalContent>
        <ModalHeader pb={2}>
          <Flex align="center" gap={2}>
            {selectedLink && (
              <IconButton
                aria-label="Voltar"
                icon={<ArrowLeft size={18} />}
                size="sm"
                variant="ghost"
                onClick={() => setSelectedLink(null)}
                mr={1}
              />
            )}
            <Icon as={selectedLink ? Eye : LinkIcon} boxSize={5} color="blue.500" />
            <Text fontSize="lg">{selectedLink ? 'Historico de Acessos' : 'Links Temporarios'}</Text>
          </Flex>
          {selectedLink && (
            <Text fontSize="xs" color="gray.500" mt={1} ml={10}>
              Link: {selectedLink.hashed_link.substring(0, 20)}...
            </Text>
          )}
        </ModalHeader>
        <ModalCloseButton />

        <Divider />

        <ModalBody py={4}>{selectedLink ? renderAcessosView() : renderLinksList()}</ModalBody>
      </ModalContent>
    </Modal>
  );
}
